import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { FileText, ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import subscriptionService from '../../services/subscription';
import type { SubscriptionTransaction } from '../../types/subscription';
import { useAuth } from '../../context/AuthContext';
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import { isOrgAdmin } from '@/utils/auth';

function InvoiceHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgTz = useOrgTimezone();
  const isAdmin = isOrgAdmin(user);

  const [invoices, setInvoices] = useState<SubscriptionTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/subscriptions', { replace: true });
      return;
    }
    loadInvoices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await subscriptionService.getInvoiceHistory();
      if (res.success) {
        setInvoices(res.data);
      }
    } catch {
      setError('Failed to load invoice history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Invoice History"
          subtitle="View invoices generated for your subscriptions"
          icon={<FileText className="h-6 w-6 text-white" />}
        />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Invoice History"
        subtitle="View invoices generated for your subscriptions"
        icon={<FileText className="h-6 w-6 text-white" />}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/subscriptions')}>
            <ArrowLeft className="h-4 w-4" />
            Back to Subscriptions
          </Button>
        </div>

        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-xl p-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-error-600" />
            <p className="text-error-700">{error}</p>
          </div>
        )}

        {invoices.length === 0 ? (
          <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
            <FileText className="h-12 w-12 text-neutral-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-1">No invoices yet</h3>
            <p className="text-neutral-500">
              Invoices will appear here when you purchase or upgrade subscriptions.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50 border-b border-neutral-200">
                    <th className="text-left px-6 py-3 font-medium text-neutral-600">Invoice #</th>
                    <th className="text-left px-6 py-3 font-medium text-neutral-600">Date</th>
                    <th className="text-left px-6 py-3 font-medium text-neutral-600">Description</th>
                    <th className="text-left px-6 py-3 font-medium text-neutral-600 whitespace-nowrap">Amount ex GST</th>
                    <th className="text-left px-6 py-3 font-medium text-neutral-600">Status</th>
                    <th className="text-left px-6 py-3 font-medium text-neutral-600">Your PO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {invoices.map(inv => (
                    <tr key={inv.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-neutral-900">
                        {inv.xero_invoice_number || <span className="text-neutral-400 font-normal">Pending</span>}
                      </td>
                      <td className="px-6 py-4 text-neutral-600">
                        {inv.processed_at ? formatDateTimeInTz(inv.processed_at, orgTz) : formatDateTimeInTz(inv.created_at, orgTz)}
                      </td>
                      <td className="px-6 py-4 text-neutral-600 max-w-xs truncate">
                        {inv.description}
                      </td>
                      <td className="px-6 py-4 font-medium text-neutral-900">
                        {inv.amount ? subscriptionService.formatCurrency(Number(inv.amount), inv.currency || 'AUD') : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          // Customer wording — the raw accounting status is
                          // for the billing team, not the org admin.
                          const st = subscriptionService.customerInvoiceStatus(inv.xero_invoice_status);
                          return (
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${st.className}`}>
                              {st.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-neutral-500">
                        {inv.payment_reference || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InvoiceHistory;
